import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  getProcessedReport, 
  getAllReports, 
  getFilterOptions,
  uploadReport,
  deleteReport 
} from "../core/api/report";
import { fetchBrandsBySelectedClients } from "../core/api/settings";

// Query Keys
export const QUERY_KEYS = {
  PROCESSED_DATA: 'processedData',
  ALL_REPORTS: 'allReports',
  FILTER_OPTIONS: 'filterOptions',
  BRANDS: 'brands',
};

// Hook for fetching processed report data
export const useProcessedData = (fileId, filters, requestType = "process", compareFileId = null, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PROCESSED_DATA, fileId, filters, requestType, compareFileId],
    queryFn: () => getProcessedReport(fileId, filters, requestType, compareFileId),
    enabled: enabled && !!fileId,
    staleTime: 150 * 60 * 1000, // 5 minutes
    cacheTime: 150 * 60 * 1000, // 10 minutes
    retry: 1,
    select: (data) => data.data, // Extract the data property
  });
};

// Hook for fetching all reports
export const useAllReports = (filters, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ALL_REPORTS, filters],
    queryFn: () => getAllReports(filters),
    enabled: enabled && filters?.client_ids?.length > 0,
    staleTime: 120 * 60 * 1000, // 2 minutes
    select: (data) => {
      const formattedFiles = data.data?.map((file) => ({
        value: file.id,
        label: "Uploaded on " + 
               new Date(file.created_at).toDateString() + 
               " at " + 
               new Date(file.created_at).toLocaleTimeString(),
      }));
      return formattedFiles || [];
    },
  });
};

// Hook for fetching filter options
export const useFilterOptions = (fileId, filters, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FILTER_OPTIONS, fileId, filters],
    queryFn: () => getFilterOptions(fileId, filters),
    enabled: enabled && !!fileId,
      staleTime: 120 * 60 * 1000, // 1 minute
    cacheTime: 120 * 60 * 1000, // 1 minute
    select: (data) => data.data?.available_filters || {},
  });
};

// Hook for fetching brands by selected clients
export const useBrands = (selectedClients, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.BRANDS, selectedClients],
    queryFn: () => fetchBrandsBySelectedClients(selectedClients),
    enabled: enabled && selectedClients?.length > 0,
      staleTime: 120 * 60 * 1000, // 5 minutes
    cacheTime: 120 * 60 * 1000, // 5 minutes
    select: (data) => data.data || [],
  });
};

// Mutation hook for uploading reports
export const useUploadReport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: uploadReport,
    onSuccess: () => {
      // Invalidate and refetch reports
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_REPORTS] });
    },
  });
};

// Mutation hook for deleting reports
export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteReport,
    onSuccess: () => {
      // Invalidate and refetch reports
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_REPORTS] });
    },
  });
};

// Hook for manual refetch of processed data
export const useRefreshProcessedData = () => {
  const queryClient = useQueryClient();
  
  return (fileId, filters, requestType = "process", compareFileId = null) => {
    return queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.PROCESSED_DATA, fileId, filters, requestType, compareFileId]
    });
  };
};
